import { Button } from "@/components/ui/button";
import { JSX, useState } from "react";

import { IManagerCandidate } from "@/@types/divergence";

import {
  BATCH_ROW_SUMMARY_TEST_ID,
  BATCH_ROW_TEST_ID,
} from "../../utils/batch-row-test-ids";
import { BatchRow } from "../../hooks/use-batch-approval";
import {
  EmploymentNotice,
  employmentNoticeOf,
} from "../../utils/manual-identity";
import {
  BATCH_APPROVAL_LABELS,
  MANUAL_IDENTITY_LABELS,
} from "../../utils/messages";
import { BatchApprovalRowProps } from "./batch-approval-types";
import { BatchRowHeader } from "./batch-row-header";
import { EmployeeRegistryPanel } from "./employee-registry-panel";
import { EmploymentNoticeBanner } from "./employment-notice-banner";
import { IdentityRuleAcknowledge } from "./identity-rule-acknowledge";
import { InstallmentsField } from "./installments-field";
import { ManualIdentityFallback } from "./manual-identity-fallback";

const blockMessageOf = (row: BatchRow, showSummary: boolean): string => {
  if (row.resolved?.preview) return MANUAL_IDENTITY_LABELS.REPLACEMENT_INTRO;
  if (showSummary) return MANUAL_IDENTITY_LABELS.INTRO;

  return row.resolved?.message ?? MANUAL_IDENTITY_LABELS.INTRO;
};

const summaryOf = (row: BatchRow): string => {
  if (!row.effective) {
    if (row.resolved?.message) return row.resolved.message;

    return row.resolved?.identity_failure
      ? BATCH_APPROVAL_LABELS.UNRESOLVED
      : BATCH_APPROVAL_LABELS.LOADING;
  }

  const parts: string[] = [
    row.effective.identity.name,
    row.effective.registry.position ?? "",
    row.effective.registry.situation ?? "",
  ];

  return parts.filter((part: string): boolean => part.trim().length > 0).join(" · ");
};

export function BatchApprovalRow({
  row,
  item,
  onInstallments,
  onCpf,
  onChosen,
  onAckRule,
  onAckEmployment,
  onSearch,
}: BatchApprovalRowProps): JSX.Element {
  const [chosenOpen, setChosenOpen] = useState<boolean | null>(null);
  const [replacing, setReplacing] = useState<boolean>(false);

  const notice: EmploymentNotice | null = row.effective
    ? employmentNoticeOf(row.effective.employment)
    : null;

  const canInform: boolean = Boolean(row.resolved?.manual_identity_allowed);

  const byCpf: boolean = replacing || (!row.resolved?.preview && canInform);

  const needsCpf: boolean = !row.resolved?.preview && canInform;

  const open: boolean = chosenOpen ?? needsCpf;

  const canToggle: boolean = Boolean(row.resolved?.preview);

  const effective = row.effective;

  const knownOperator: IManagerCandidate | null =
    row.resolved?.known_operator ?? null;

  const showSummary: boolean = !open || !knownOperator;

  const blockMessage: string = blockMessageOf(row, showSummary);

  return (
    <div
      data-testid={BATCH_ROW_TEST_ID(row.id)}
      className="flex flex-col gap-3 rounded-lg border bg-gray-50 p-3"
    >
      <BatchRowHeader id={row.id} item={item} />

      {showSummary && (
        <p
          data-testid={BATCH_ROW_SUMMARY_TEST_ID(row.id)}
          className={`text-sm ${row.effective ? "text-gray-700" : "text-amber-700"}`}
        >
          {summaryOf(row)}
        </p>
      )}

      {row.needsRuleAck && row.resolved?.source && (
        <IdentityRuleAcknowledge
          source={row.resolved.source}
          acknowledged={row.ruleAcked}
          onAcknowledge={onAckRule}
        />
      )}

      <div className="flex flex-wrap gap-2">
        {canToggle && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={(): void => setChosenOpen(!open)}
          >
            {open
              ? BATCH_APPROVAL_LABELS.DETAILS_HIDE
              : BATCH_APPROVAL_LABELS.DETAILS_SHOW}
          </Button>
        )}

        {row.resolved?.preview && !replacing && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={(): void => {
              setReplacing(true);
              setChosenOpen(true);
            }}
          >
            {BATCH_APPROVAL_LABELS.REPLACE}
          </Button>
        )}
      </div>

      {open && byCpf && (
        <ManualIdentityFallback
          blockMessage={blockMessage}
          candidates={row.resolved?.identity_candidates ?? []}
          knownOperator={knownOperator}
          preview={row.state.informed}
          isSearching={row.state.isSearching}
          searchError={row.state.searchError}
          cpf={row.state.cpf}
          chosen={row.state.chosen}
          acknowledgedFor={row.state.employmentAckedFor}
          candidatesFailed={row.state.candidatesFailed}
          onCpfChange={onCpf}
          onChosenChange={onChosen}
          onAcknowledgeChange={onAckEmployment}
          onSearch={onSearch}
        />
      )}

      {open && !byCpf && effective && (
        <div className="flex flex-col gap-3">
          <EmployeeRegistryPanel preview={effective} />
          {notice && (
            <EmploymentNoticeBanner
              notice={notice}
              acknowledged={
                row.state.employmentAckedFor === effective.identity.cpf
              }
              onAcknowledge={(value: boolean): void =>
                onAckEmployment(value ? effective.identity.cpf : "")
              }
            />
          )}
        </div>
      )}

      {!open && notice && (
        <EmploymentNoticeBanner
          notice={notice}
          acknowledged={row.state.employmentAckedFor === effective?.identity.cpf}
          onAcknowledge={(value: boolean): void =>
            onAckEmployment(value ? (effective?.identity.cpf ?? "") : "")
          }
        />
      )}

      <InstallmentsField
        id={`batch-installments-${row.id}`}
        amount={item.amount}
        value={row.state.installments}
        disabled={false}
        showHint={false}
        onChange={onInstallments}
      />
    </div>
  );
}
